import React, {Suspense} from 'react';
import {useSelector} from 'react-redux';
import {isEqual} from 'lodash';
import {useAresUrlParameters} from '../../../../util/Ares/customHooks';
import {useMFE} from '../../../../util/customHooks';
import {getTaskDocumentByDocumentId} from '../../../../selectors/Ares/selectors';
import {OBSERVERS_CONFIG_BY_DOCUMENT_TYPE, DEFAULT_OBSERVERS_KEY} from './config';

/**
 * Top-level host that mounts the appropriate observer component(s) for the
 * current Ares canvas form. Two sources are merged:
 *  1. Default observers registered under DEFAULT_OBSERVERS_KEY, mounted on
 *     every Ares document.
 *  2. Document-type-specific observers registered under the active
 *     documentTypeId, mounted only when the user is on that doc type.
 * One component is mounted per config entry so each watches its own slice
 * and owns its own host-notification contract independently. Renders nothing
 * when the user is outside Ares or when neither source has entries for the
 * current document.
 *
 * Lives in App.js layout (above Routes) so it doesn't remount when the Ares
 * URL pattern changes (e.g. documentId/header/section navigation), which
 * would otherwise reset the diff baselines mid-form.
 *
 * The observer pipeline only makes sense when Ares is embedded as an MFE
 * inside Hercules - that's the only consumer of the broadcasts. In Ares'
 * standalone mode there is no host to notify, so the entire host is a no-op.
 *
 * Each observer component is lazy-loaded so document types not visited in
 * the current session never pull their selectors/bridge code into the
 * initial bundle. A Suspense boundary with a null fallback covers the
 * micro-delay between mount and chunk arrival; observers render nothing
 * anyway, so there's no visible flash.
 *
 * Adding new document types or extra observers is a config append in
 * ./config.js. No changes here.
 */
const AresCanvasObserverHost = () => {
	const [isMFE] = useMFE();
	const {documentId} = useAresUrlParameters();
	const document = useSelector((state) => getTaskDocumentByDocumentId(state, documentId), isEqual);
	const documentTypeId = document?.documentTypeId;

	if (!isMFE || !documentId) return null;

	const defaultConfigs = OBSERVERS_CONFIG_BY_DOCUMENT_TYPE[DEFAULT_OBSERVERS_KEY] || [];
	const documentTypeConfigs = OBSERVERS_CONFIG_BY_DOCUMENT_TYPE[documentTypeId] || [];
	const observerConfigs = [...defaultConfigs, ...documentTypeConfigs];

	if (observerConfigs.length === 0) return null;

	return (
		<Suspense fallback={null}>
			{observerConfigs.map(({name, component: ObserverComponent}) => (
				<ObserverComponent key={name} />
			))}
		</Suspense>
	);
};

export default AresCanvasObserverHost;
